import type { AccountId } from "@repo/app-types/entities/account";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { createContext, type FC, type ReactNode, useContext } from "react";
import {
  InteractiveReferenceBoundary,
  useIsInsideInteractiveReference,
} from "./interactiveReferenceBoundary.js";

export interface CustomerReference {
  accountId: AccountId;
  /**
   * The playbook whose customer page the reference links into, when the
   * content or its surface knows one. The host picks a fallback otherwise.
   */
  playbookId?: PlaybookId;
}

export interface CustomerReferenceWrapperProps {
  reference: CustomerReference;
  /** Base styling shared by the linked and inert reference. */
  className: string;
  children: ReactNode;
}

/**
 * An app-supplied component that turns a customer reference into a link to
 * the customer's page. Apps that don't supply one render references as
 * styled text.
 */
export type CustomerReferenceWrapper = FC<CustomerReferenceWrapperProps>;

const CustomerReferenceContext = createContext<
  CustomerReferenceWrapper | undefined
>(undefined);

export const CustomerReferenceProvider = CustomerReferenceContext.Provider;

export interface CustomerReferenceNodeProps {
  /** Absent when the customer has no account to link to. */
  reference: CustomerReference | undefined;
  className: string;
  children: ReactNode;
}

/**
 * Renders a customer reference from the AST: through the host wrapper from
 * `CustomerReferenceProvider` when it has an account and one is supplied,
 * otherwise as styled text. Inside another interactive reference it renders
 * as plain content. Exists as a component so the plain-function AST renderer
 * can consume the context.
 */
export const CustomerReferenceNode: FC<CustomerReferenceNodeProps> = (
  props,
) => {
  const { reference, className, children } = props;
  const Wrapper = useContext(CustomerReferenceContext);
  const isInsideInteractiveReference = useIsInsideInteractiveReference();
  if (isInsideInteractiveReference) {
    return <>{children}</>;
  }
  if (!reference || !Wrapper) {
    return <span className={className}>{children}</span>;
  }
  return (
    <Wrapper reference={reference} className={className}>
      <InteractiveReferenceBoundary>{children}</InteractiveReferenceBoundary>
    </Wrapper>
  );
};
